import {
  AppendMessage,
  AssistantRuntimeProvider as Provider,
  ThreadMessageLike,
  useExternalStoreRuntime,
} from '@assistant-ui/react'
import { createContext, ReactNode, useCallback, useContext, useEffect, useRef, useState } from 'react'
import {
  AssistantDraftMode,
  AssistantMessage,
  AssistantPromptFile,
  AssistantStep,
  createAssistantConversation,
  getAssistantConversations,
  getAssistantModel,
  newAssistantTurnId,
  postAssistantPrompt,
  stopAssistantPrompt,
  streamAssistantPrompt,
} from 'services/apis/ai'
import {
  AiArtifact,
  AiConversation,
  AiConversationMessage,
  AiProject,
  createAiProjectConversation,
  deleteAiConversation,
  getAiConversationMessages,
  getAiProjectArtifacts,
  getAiProjectConversations,
  getAiProjects,
  updateAiConversation,
} from 'services/apis/aiProjects'
import { confirm } from 'utils'

import { mergeStep } from './AssistantSteps'
import { useActiveLoanNumber } from './useActiveLoanNumber'
import { useVoiceAssistant, VoiceAssistant } from './voice'

const convertMessage = (message: AssistantMessage): ThreadMessageLike => {
  const custom: Record<string, unknown> = {}
  if (message.files?.length) custom.files = message.files
  if (message.steps?.length) custom.steps = message.steps
  return {
    role: message.role === 'model' ? 'assistant' : message.role,
    content: [{ type: 'text', text: message.content }],
    id: message.id,
    createdAt: new Date(message.createdAt),
    metadata: Object.keys(custom).length ? { custom } : undefined,
  }
}

const toAssistantMessage = (message: AiConversationMessage): AssistantMessage => ({
  id: String(message.id),
  role: message.role,
  content: message.content,
  createdAt: message.createdAt,
  files: message.files?.length ? message.files : undefined,
  steps: message.steps?.length ? message.steps : undefined,
  // Carried so a reopened thread replays its tool calls to the model, not just its prose.
  blocks: message.blocks?.length ? message.blocks : undefined,
})

/** A message sent while a turn was still running. It goes out as its own turn once that one finishes. */
export interface QueuedPrompt {
  id: string
  text: string
  files: AssistantPromptFile[]
}

export interface AssistantAttachmentsContextValue {
  files: AssistantPromptFile[]
  addFiles: (files: AssistantPromptFile[]) => void
  removeFile: (fileKey: string) => void
  clearFiles: () => void
  isUploading: boolean
  setIsUploading: (value: boolean) => void
  isRunning: boolean
  /** Messages waiting for the turn in flight to finish, oldest first. */
  queuedPrompts: QueuedPrompt[]
  removeQueuedPrompt: (id: string) => void
  /** A live "what it's doing" phrase while running, or '' (then the UI shows a generic spinner). */
  runningStatus: string
  /**
   * The steps of the turn in flight, updated as each tool call starts and again when it returns,
   * so the panel shows the work as it happens rather than a spinner and then everything at once.
   * Empty when nothing is running; the finished turn keeps its own copy on the message.
   */
  liveSteps: AssistantStep[]
  /**
   * The answer so far, while it is still being written. '' when nothing is streaming - either
   * between turns, or because this environment's model transport cannot stream, in which case the
   * turn still ends with the whole answer at once, exactly as it did before.
   */
  streamingText: string
  /**
   * The model answering, shown in the header. '' until loaded: predicted before the first turn,
   * then replaced by the one each answer reports, since the assistant can fall back between paths.
   */
  model: string
  draftMode: AssistantDraftMode
  setDraftMode: (value: AssistantDraftMode) => void
  /** Projects the user can load into this chat. Empty while loading, or if none are readable. */
  projects: AiProject[]
  /** The loaded project, or 0 for a plain chat with no project context. */
  projectId: number
  setProjectId: (value: number) => void
  /** The loan the user is on, or 0. Every turn carries it so tools default to that loan. */
  loanNumber: number
  /**
   * The loaded project's artifacts for this context: on a loan, that loan's own plus the project's
   * unscoped ones. Empty without a project, because an artifact always belongs to one.
   */
  artifacts: AiArtifact[]
  /** Re-read the artifact list. Called after every turn, since a turn may have written one. */
  reloadArtifacts: () => void
  /** Saved threads for the current project/loan context. */
  conversations: AiConversation[]
  activeConversationId: number | null
  conversationsLoading: boolean
  startNewConversation: () => Promise<void>
  selectConversation: (conversationId: number) => Promise<void>
  deleteConversation: (conversation: AiConversation) => Promise<void>
  /** Owner-only: flip a chat between public (team-visible) and private. */
  setConversationVisibility: (conversation: AiConversation, visibility: 'public' | 'private') => Promise<void>
  /** Hands-free voice: wake word, dictation, and spoken answers. */
  voice: VoiceAssistant
  /**
   * Run one turn in this chat, as if it had been typed. For controls that ask for work outside the
   * composer - Run project, which sends the project's own instructions - and it resolves with the
   * answer, so a caller that needs the text has it. The thread, the live steps and Stop are all the
   * panel's own, exactly as for a typed turn.
   */
  sendPrompt: (prompt: string) => Promise<string>
  /** The thread on screen. Export uses the numeric ids from saved rows. */
  messages: AssistantMessage[]
}

export const AssistantAttachmentsContext = createContext<AssistantAttachmentsContextValue | null>(null)

export const useAssistantAttachments = () => {
  const context = useContext(AssistantAttachmentsContext)
  if (!context) {
    throw new Error('useAssistantAttachments must be used within AssistantRuntimeProvider')
  }
  return context
}

interface AssistantRuntimeProviderProps {
  children: ReactNode
  context?: string
  /**
   * Whether voice may hold the microphone. Signed out, it must not - the assistant is mounted on
   * the login screen too, and an open mic there would be indefensible.
   */
  voiceEnabled?: boolean
  /** Raised when the wake phrase lands, so the panel can come forward before the question ends. */
  onVoiceWake?: () => void
}

export function AssistantRuntimeProvider({
  children,
  context,
  voiceEnabled = false,
  onVoiceWake,
}: AssistantRuntimeProviderProps) {
  const [isRunning, setIsRunning] = useState(false)
  // Mirrors isRunning synchronously. State lags a render, and a second send in that gap must see
  // that a turn has already started rather than run beside it.
  const runningRef = useRef(false)
  const [queuedPrompts, setQueuedPrompts] = useState<QueuedPrompt[]>([])
  const [runningStatus, setRunningStatus] = useState('')
  const [liveSteps, setLiveSteps] = useState<AssistantStep[]>([])
  const [streamingText, setStreamingText] = useState('')
  const [messages, setMessages] = useState<AssistantMessage[]>([])
  const [pendingFiles, setPendingFiles] = useState<AssistantPromptFile[]>([])
  const [isUploading, setIsUploading] = useState(false)
  const [draftMode, setDraftMode] = useState<AssistantDraftMode>('values')
  const [model, setModel] = useState('')
  const [projects, setProjects] = useState<AiProject[]>([])
  const [projectId, setProjectId] = useState(0)
  const [artifacts, setArtifacts] = useState<AiArtifact[]>([])
  const [conversations, setConversations] = useState<AiConversation[]>([])
  const [activeConversationId, setActiveConversationId] = useState<number | null>(null)
  const [conversationsLoading, setConversationsLoading] = useState(false)
  const abortRef = useRef<AbortController | null>(null)
  /** The id of the turn in flight, so Stop can name it to the server. */
  const turnIdRef = useRef('')
  const activeConversationIdRef = useRef<number | null>(null)
  const loanNumber = useActiveLoanNumber()

  activeConversationIdRef.current = activeConversationId

  useEffect(() => {
    getAiProjects()
      .then((data) => setProjects(data || []))
      .catch(() => {})
  }, [])

  useEffect(() => {
    getAssistantModel()
      .then((info) => setModel(info.model || info.engine))
      .catch(() => {})
  }, [])

  /**
   * Artifacts are project-scoped, so a plain chat with no project has none to show. Inside a loan
   * the request is narrowed to it and the server adds back the project's unscoped documents.
   */
  const reloadArtifacts = useCallback(() => {
    if (!projectId) {
      setArtifacts([])
      return
    }
    getAiProjectArtifacts(projectId, loanNumber || undefined)
      .then((data) => setArtifacts(data || []))
      .catch(() => {})
  }, [projectId, loanNumber])

  useEffect(() => {
    reloadArtifacts()
  }, [reloadArtifacts])

  const loadConversationMessages = useCallback(async (conversationId: number) => {
    const data = await getAiConversationMessages(conversationId).catch(() => [] as AiConversationMessage[])
    setMessages((data || []).map(toAssistantMessage))
  }, [])

  const reloadConversations = useCallback(
    async (selectMostRecent = false) => {
      setConversationsLoading(true)
      try {
        const data = projectId
          ? await getAiProjectConversations(projectId, loanNumber || undefined).catch(() => [] as AiConversation[])
          : await getAssistantConversations(loanNumber || undefined).catch(() => [] as AiConversation[])
        // Loan page → that loan's threads only. Pipeline / other screens → unscoped (global) only,
        // so a loan chat does not linger after you leave the loan.
        const list = (data || []).filter((item) =>
          loanNumber ? item.loanNumber === loanNumber : !item.loanNumber,
        )
        setConversations(list)
        if (selectMostRecent && list.length) {
          const nextId = list[0].id
          setActiveConversationId(nextId)
          await loadConversationMessages(nextId)
        } else if (!list.some((item) => item.id === activeConversationIdRef.current)) {
          setActiveConversationId(null)
          setMessages([])
        }
        return list
      } finally {
        setConversationsLoading(false)
      }
    },
    [projectId, loanNumber, loadConversationMessages],
  )

  useEffect(() => {
    reloadConversations(true)
  }, [projectId, loanNumber, reloadConversations])

  const createConversation = useCallback(async () => {
    const conversation = projectId
      ? await createAiProjectConversation(projectId, undefined, loanNumber || undefined)
      : await createAssistantConversation(loanNumber || undefined)
    setConversations((prev) => [conversation, ...prev])
    setActiveConversationId(conversation.id)
    setMessages([])
    return conversation
  }, [projectId, loanNumber])

  const startNewConversation = useCallback(async () => {
    await createConversation()
  }, [createConversation])

  const selectConversation = useCallback(
    async (conversationId: number) => {
      if (conversationId === activeConversationIdRef.current) return
      setActiveConversationId(conversationId)
      await loadConversationMessages(conversationId)
    },
    [loadConversationMessages],
  )

  const deleteConversation = useCallback(
    async (conversation: AiConversation) => {
      const content = (
        <div className="text-gray-600 mb-4 text-md">
          Are you sure you want to delete &ldquo;{conversation.title}&rdquo;?
        </div>
      )
      const result = await confirm(content)
      if (!result) return
      await deleteAiConversation(conversation.id)
      const remaining = conversations.filter((item) => item.id !== conversation.id)
      setConversations(remaining)
      if (activeConversationIdRef.current === conversation.id) {
        const nextId = remaining[0]?.id ?? null
        setActiveConversationId(nextId)
        if (nextId) await loadConversationMessages(nextId)
        else setMessages([])
      }
    },
    [conversations, loadConversationMessages],
  )

  const setConversationVisibility = useCallback(
    async (conversation: AiConversation, visibility: 'public' | 'private') => {
      const updated = await updateAiConversation(conversation.id, { visibility })
      setConversations((prev) => prev.map((item) => (item.id === conversation.id ? { ...item, ...updated } : item)))
    },
    [],
  )

  const addFiles = useCallback((files: AssistantPromptFile[]) => {
    setPendingFiles((prev) => {
      const existingKeys = new Set(prev.map((file) => file.fileKey))
      const nextFiles = files.filter((file) => !existingKeys.has(file.fileKey))
      return [...prev, ...nextFiles]
    })
  }, [])

  const removeFile = useCallback((fileKey: string) => {
    setPendingFiles((prev) => prev.filter((file) => file.fileKey !== fileKey))
  }, [])

  const clearFiles = useCallback(() => {
    setPendingFiles([])
  }, [])

  const ensureConversation = useCallback(async () => {
    if (activeConversationIdRef.current) return activeConversationIdRef.current
    const conversation = await createConversation()
    return conversation.id
  }, [createConversation])

  /**
   * One turn, whether it was typed or spoken. Voice needs the answer back to read it out, so this
   * resolves with the assistant's text rather than only pushing it into the thread - and it
   * resolves with the failure message too, because "an error occurred" is worth hearing.
   */
  const sendPrompt = useCallback(
    async (input: string, attachedFiles: AssistantPromptFile[], options?: { voice?: boolean }): Promise<string> => {
      if (!input && attachedFiles.length === 0) return ''

      const displayContent =
        input ||
        `Please analyze the attached file${attachedFiles.length > 1 ? 's' : ''}: ${attachedFiles.map((file) => file.name).join(', ')}`

      const userMsg: AssistantMessage = {
        id: `user-${Date.now()}`,
        role: 'user',
        content: displayContent,
        createdAt: new Date().toISOString(),
        files: attachedFiles.length > 0 ? attachedFiles : undefined,
      }

      setIsRunning(true)
      runningRef.current = true
      abortRef.current = new AbortController()
      // Names this turn so Stop can reach it on the server. Aborting the request alone no longer
      // stops the work: the server keeps a turn going when the connection drops, which is what
      // makes a reload or a closed tab harmless rather than a half-written answer.
      const turnId = newAssistantTurnId()
      turnIdRef.current = turnId

      // The answer as it streams in. Held in a plain variable as well as state because deltas
      // arrive far faster than React re-renders, and declared out here so a stopped turn can keep
      // what was already written - the server saves the same partial answer to the thread.
      let streamedText = ''
      const localSteps: AssistantStep[] = []

      try {
        // Create the saved thread before showing the user turn. createConversation clears messages,
        // so doing this after the optimistic append would wipe what the user just typed.
        const conversationId = await ensureConversation()
        const historyForPrompt = [...messages, userMsg]

        setMessages((prev) => [...prev, userMsg])
        clearFiles()

        const requestBody = {
          prompt: input || displayContent,
          history: historyForPrompt,
          context,
          promptFiles: attachedFiles.length > 0 ? attachedFiles : undefined,
          draftMode,
          projectId: projectId || undefined,
          conversationId: conversationId || undefined,
          turnId,
          loanNumber: loanNumber || undefined,
          voice: options?.voice || undefined,
        }
        // Stream so the panel shows what the assistant is doing live. If streaming fails for any
        // reason (a proxy that buffers, a transient network error), fall back to the plain endpoint
        // so the turn still completes - just without the live status.
        let resData: AssistantMessage | string
        // The server is the source of truth for the "what it did" steps (it also persists them), but
        // keep a local copy of the streamed ones as a fallback in case the payload omits them.
        let serverSteps: Array<string | AssistantStep> | undefined
        let serverBlocks: any[] | undefined
        const recordStatus = (text: string) => setRunningStatus(text)
        // A step arrives twice - once when the call starts, once with its result - so the second
        // one updates the card in place instead of adding a duplicate row. This is also what makes
        // the panel fill in live rather than all at once at the end.
        const recordStep = (step: AssistantStep) => {
          setLiveSteps([...mergeStep(localSteps, step)])
        }
        setLiveSteps([])
        const recordDelta = (delta: { text?: string; reset?: boolean }) => {
          // A new step of the agent loop: what came before it was narration before a tool call, and
          // its step card already records it. Start the answer over rather than gluing them together.
          if (delta.reset) streamedText = ''
          if (delta.text) streamedText += delta.text
          setStreamingText(streamedText)
        }
        setStreamingText('')
        try {
          const streamed = await streamAssistantPrompt(requestBody, {
            onStatus: recordStatus,
            onStep: recordStep,
            onDelta: recordDelta,
            signal: abortRef.current?.signal,
          })
          resData = streamed.data
          serverSteps = streamed.steps
          serverBlocks = streamed.blocks
          if (streamed.model) setModel(streamed.model)
        } catch (streamErr: any) {
          if (streamErr?.name === 'AbortError') throw streamErr
          setRunningStatus('')
          setStreamingText('')
          // Re-running is only safe before the turn has done anything. Once steps have run, the
          // assistant may already have written something - an artifact, a note, a queued action -
          // and asking again would do it twice. A stream that breaks mid-turn usually means the
          // connection was dropped rather than the work failing, and the server finishes the turn
          // and saves it, so the answer is fetched from the thread instead of being asked for again.
          if (localSteps.length) {
            if (conversationId) await loadConversationMessages(conversationId)
            throw new Error('connection-lost')
          }
          const response = await postAssistantPrompt(requestBody)
          resData = response.data
          serverSteps = response.steps
          serverBlocks = response.blocks
          if (response.model) setModel(response.model)
        }
        setLiveSteps([])
        const steps: Array<string | AssistantStep> = serverSteps?.length ? serverSteps : localSteps
        const assistantMsg: AssistantMessage =
          typeof resData === 'object' && resData?.role
            ? resData
            : {
                id: `model-${Date.now()}`,
                role: 'model',
                content: typeof resData === 'string' ? resData : JSON.stringify(resData ?? ''),
                createdAt: new Date().toISOString(),
                steps: steps.length ? steps : undefined,
                blocks: serverBlocks?.length ? serverBlocks : undefined,
              }
        setMessages((prev) => [...prev, assistantMsg])
        // Reload so each turn carries its persisted id - export to Documents keys off those ids,
        // and a local `model-${Date.now()}` placeholder cannot be filed.
        if (conversationId) await loadConversationMessages(conversationId)
        reloadConversations()
        return assistantMsg.content
      } catch (err: any) {
        if (err?.message === 'connection-lost') {
          // The thread was reloaded above, so the answer is already on screen if the turn finished.
          // Say what happened rather than leaving a turn that looks like it vanished.
          setMessages((prev) =>
            prev.some((message) => message.id === 'connection-lost')
              ? prev
              : [
                  ...prev,
                  {
                    id: 'connection-lost',
                    role: 'model',
                    content:
                      'The connection to the assistant dropped while it was working. It carries on ' +
                      'server-side, so reopen this chat in a moment to see the finished answer.',
                    createdAt: new Date().toISOString(),
                  },
                ],
          )
          return ''
        }
        if (err?.name === 'AbortError') {
          // Stop keeps what was written rather than throwing it away mid-sentence. The server saves
          // the same text on its side, so the thread reads the same after a reload.
          const partial = streamedText.trim()
          if (partial) {
            setMessages((prev) => [
              ...prev,
              {
                id: `model-${Date.now()}`,
                role: 'model',
                content: `${partial}\n\n_Stopped._`,
                createdAt: new Date().toISOString(),
                steps: localSteps.length ? localSteps : undefined,
              },
            ])
          }
          return partial
        }
        const failure = 'An error occurred. Please try again.'
        setMessages((prev) => [
          ...prev,
          {
            id: `error-${Date.now()}`,
            role: 'model',
            content: failure,
            createdAt: new Date().toISOString(),
          },
        ])
        return failure
      } finally {
        // In `finally` rather than beside the success path: a turn whose stream dropped carries on
        // server-side and may well have saved the artifact, which is exactly when a stale list is
        // most misleading.
        reloadArtifacts()
        setIsRunning(false)
        runningRef.current = false
        setRunningStatus('')
        // The finished message carries the answer now; leaving the live copy up would show it twice.
        setStreamingText('')
        abortRef.current = null
        turnIdRef.current = ''
      }
    },
    [
      messages,
      context,
      clearFiles,
      draftMode,
      projectId,
      loanNumber,
      ensureConversation,
      reloadConversations,
      reloadArtifacts,
      loadConversationMessages,
    ],
  )

  const onNew = useCallback(
    async (message: AppendMessage) => {
      const firstPart = message.content[0]
      const input = firstPart?.type === 'text' ? firstPart.text.trim() : ''
      const files = [...pendingFiles]
      if (runningRef.current) {
        // A turn is in flight: hold this message and send it when that turn finishes.
        if (!input && files.length === 0) return
        setQueuedPrompts((prev) => [
          ...prev,
          { id: `queued-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, text: input, files },
        ])
        clearFiles()
        return
      }
      await sendPrompt(input, files)
    },
    [pendingFiles, sendPrompt, clearFiles],
  )

  const sendTypedPrompt = useCallback((prompt: string) => sendPrompt(prompt, []), [sendPrompt])

  const sendVoicePrompt = useCallback(
    (prompt: string, options: { voice: boolean }) => sendPrompt(prompt, [], options),
    [sendPrompt],
  )

  const voice = useVoiceAssistant({
    enabled: voiceEnabled,
    sendPrompt: sendVoicePrompt,
    onWake: onVoiceWake,
  })

  const onCancel = useCallback(async () => {
    // Tell the server first: the abort below only ends OUR request, and a request that simply ends
    // is not a stop any more. Then abort, so the panel stops immediately either way.
    const turnId = turnIdRef.current
    if (turnId) stopAssistantPrompt(turnId)
    abortRef.current?.abort()
    runningRef.current = false
    setIsRunning(false)
    // Stop means stop: messages queued behind the stopped turn are dropped, not sent on its heels.
    setQueuedPrompts([])
  }, [])

  // Send the next queued message once the turn in flight has finished. One at a time, because each
  // is a full turn that may use tools, and the answer to one can change what the next should say.
  useEffect(() => {
    if (isRunning || queuedPrompts.length === 0) return
    const [next, ...rest] = queuedPrompts
    setQueuedPrompts(rest)
    sendPrompt(next.text, next.files).catch(() => {})
  }, [isRunning, queuedPrompts, sendPrompt])

  const removeQueuedPrompt = useCallback((id: string) => {
    setQueuedPrompts((prev) => prev.filter((item) => item.id !== id))
  }, [])

  const runtime = useExternalStoreRuntime({
    isRunning,
    messages,
    convertMessage,
    onNew,
    onCancel,
  })

  const attachmentsContextValue: AssistantAttachmentsContextValue = {
    files: pendingFiles,
    addFiles,
    removeFile,
    clearFiles,
    isUploading,
    setIsUploading,
    isRunning,
    queuedPrompts,
    removeQueuedPrompt,
    runningStatus,
    liveSteps,
    streamingText,
    model,
    draftMode,
    setDraftMode,
    projects,
    projectId,
    setProjectId,
    loanNumber,
    artifacts,
    reloadArtifacts,
    conversations,
    activeConversationId,
    conversationsLoading,
    startNewConversation,
    selectConversation,
    deleteConversation,
    setConversationVisibility,
    voice,
    sendPrompt: sendTypedPrompt,
    messages,
  }

  return (
    <AssistantAttachmentsContext.Provider value={attachmentsContextValue}>
      <Provider runtime={runtime}>{children}</Provider>
    </AssistantAttachmentsContext.Provider>
  )
}
